/**
 * Session Drill-Down Modal/Drawer
 * Desktop: Full modal with session list and individual paths
 * Mobile/Tablet: Animated drawer from bottom
 */

import { useState, useEffect } from 'react';
import {
  X,
  Users,
  Clock,
  Monitor,
  Smartphone,
  Tablet,
  ChevronRight,
  ChevronDown,
  Hash,
} from 'lucide-react';

interface Session {
  sessionId: string;
  stitchedSessionIds?: string[]; // Array of original session IDs that were stitched together
  totalPages: number;
  firstPage: string;
  lastPage: string;
  device: string;
  userId?: string;
  utmSource?: string;
  utmCampaign?: string;
  duration?: number;
  pages?: string[];
}

interface SessionDrillDownModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourcePage: string;
  targetPage: string;
  sessions: Session[];
  totalSessions: number;
  isLoading?: boolean;
  onSessionClick: (sessionId: string, stitchedSessionIds?: string[]) => void;
  selectedSessionData?: {
    sessionId: string;
    events?: any[]; // Array of ALL events from backend
    sankey?: { nodes: any[]; links: any[] };
    summary: {
      device: string;
      duration: number;
      pages: number;
    };
  };
}

const SessionDrillDownModal: React.FC<SessionDrillDownModalProps> = ({
  isOpen,
  onClose,
  sourcePage,
  targetPage,
  sessions,
  totalSessions,
  isLoading,
  onSessionClick,
  selectedSessionData,
}) => {
  const [expandedSession, setExpandedSession] = useState<string | null>(null);
  const [expandedPageIndex, setExpandedPageIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  
  // **NEW: Pagination state**
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // **NEW: Filter state**
  const [deviceFilter, setDeviceFilter] = useState<string>('all');
  const [utmSourceFilter, setUtmSourceFilter] = useState<string>('all');
  const [minPagesFilter, setMinPagesFilter] = useState<number>(0);
  const [searchFilter, setSearchFilter] = useState<string>('');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  
  // **NEW: Reset pagination when modal opens or filters change**
  useEffect(() => {
    setCurrentPage(1);
  }, [isOpen, deviceFilter, utmSourceFilter, minPagesFilter, searchFilter]);
  
  // **NEW: Apply filters and pagination**
  const filteredSessions = sessions.filter((session) => {
    // Device filter
    if (deviceFilter !== 'all' && !session.device.toLowerCase().includes(deviceFilter.toLowerCase())) {
      return false;
    }
    
    // UTM Source filter
    if (utmSourceFilter !== 'all' && session.utmSource !== utmSourceFilter) {
      return false;
    }
    
    // Min pages filter
    if (minPagesFilter > 0 && session.totalPages < minPagesFilter) {
      return false;
    }
    
    // Search filter (searches session ID and user ID)
    if (searchFilter) {
      const searchLower = searchFilter.toLowerCase();
      const matchesSessionId = session.sessionId.toLowerCase().includes(searchLower);
      const matchesUserId = session.userId?.toLowerCase().includes(searchLower);
      if (!matchesSessionId && !matchesUserId) {
        return false;
      }
    }
    
    return true;
  });
  
  // **NEW: Paginate filtered sessions**
  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSessions = filteredSessions.slice(startIndex, endIndex);
  
  // **NEW: Get unique device types and UTM sources for filter dropdowns**
  const uniqueDevices = Array.from(new Set(sessions.map(s => s.device))).filter(Boolean);
  const uniqueUtmSources = Array.from(new Set(sessions.map(s => s.utmSource).filter(Boolean)));

  console.log('🎭 SessionDrillDownModal render:', {
    isOpen,
    totalSessions,
    sessionsCount: sessions?.length,
    sourcePage: sourcePage?.substring(0, 50),
    targetPage: targetPage?.substring(0, 50),
  });

  if (!isOpen) {
    console.log('❌ Modal not open - returning null');
    return null;
  }

  console.log('✅ Modal IS open - rendering...');

  const getDeviceIcon = (device: string) => {
    const deviceLower = device.toLowerCase();
    if (
      deviceLower.includes('mobile') ||
      deviceLower.includes('iphone') ||
      deviceLower.includes('android')
    ) {
      return <Smartphone className="w-4 h-4" />;
    } else if (deviceLower.includes('tablet') || deviceLower.includes('ipad')) {
      return <Tablet className="w-4 h-4" />;
    }
    return <Monitor className="w-4 h-4" />;
  };

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const handleSessionToggle = (session: Session) => {
    if (expandedSession === session.sessionId) {
      setExpandedSession(null);
    } else {
      setExpandedSession(session.sessionId);
      onSessionClick(session.sessionId, session.stitchedSessionIds);
    }
  };

  const modalContent = (
    <>
      {/* Header */}
      <div className="flex items-start justify-between p-6 border-b border-secondary-200 dark:border-secondary-700">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50 mb-2">
            User Journey Sessions
          </h2>
          {/* Show different header for node clicks vs link clicks */}
          {sourcePage === targetPage ? (
            // Node click: show "Sessions through this page"
            <div className="flex items-center gap-2 text-sm text-secondary-600 dark:text-secondary-400">
              <span className="font-medium">All sessions through:</span>
              <code className="px-2 py-1 bg-secondary-100 dark:bg-secondary-800 rounded">
                {sourcePage}
              </code>
            </div>
          ) : (
            // Link click: show "From → To"
            <div className="flex items-center gap-3 text-sm text-secondary-600 dark:text-secondary-400">
              <div className="flex items-center gap-2">
                <span className="font-medium">From:</span>
                <code className="px-2 py-1 bg-secondary-100 dark:bg-secondary-800 rounded">
                  {sourcePage}
                </code>
              </div>
              <ChevronRight className="w-4 h-4" />
              <div className="flex items-center gap-2">
                <span className="font-medium">To:</span>
                <code className="px-2 py-1 bg-secondary-100 dark:bg-secondary-800 rounded">
                  {targetPage}
                </code>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mt-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-primary-600" />
                <span className="text-sm font-medium text-primary-600">
                  {totalSessions.toLocaleString()} unique {totalSessions === 1 ? 'journey' : 'journeys'}
                </span>
              </div>
              {/* Show stitching indicator if sessions have stitchedSessionIds */}
              {sessions.some(s => s.stitchedSessionIds && s.stitchedSessionIds.length > 1) && (
                <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1 ml-6">
                  <span>✓</span>
                  <span>Multiple sessions per user combined into single journeys</span>
                </span>
              )}
            </div>
            
            {/* Info button explaining stitching */}
            {sessions.some(s => s.stitchedSessionIds && s.stitchedSessionIds.length > 1) && (
              <div className="flex items-center gap-2">
                <div className="px-3 py-1.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-yellow-700 dark:text-yellow-300">
                      ⚠️ <strong>Sessions are auto-merged</strong> when user continues journey across domains/sessions
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Note about global session counting */}
          {sourcePage === targetPage && (
            <div className="mt-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <strong>Note:</strong> Shows all sessions that visited this page, regardless of the path they took to get here.
              </p>
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-secondary-100 dark:hover:bg-secondary-800 rounded-lg transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="spinner mx-auto mb-4"></div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                Loading sessions...
              </p>
            </div>
          </div>
        ) : sessions.length > 0 ? (
          <div className="space-y-4">
            {/* **NEW: Filters and Search** */}
            <div className="sticky top-0 z-20 bg-white dark:bg-secondary-900 pt-4 pb-4 -mx-6 px-6 border-b border-secondary-200 dark:border-secondary-700 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Search */}
                <div>
                  <input
                    type="text"
                    placeholder="Search session ID..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100"
                  />
                </div>
                
                {/* Device Filter */}
                <div>
                  <select
                    value={deviceFilter}
                    onChange={(e) => setDeviceFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100"
                  >
                    <option value="all">All Devices</option>
                    {uniqueDevices.map(device => (
                      <option key={device} value={device}>{device}</option>
                    ))}
                  </select>
                </div>
                
                {/* UTM Source Filter */}
                <div>
                  <select
                    value={utmSourceFilter}
                    onChange={(e) => setUtmSourceFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100"
                  >
                    <option value="all">All Sources</option>
                    {uniqueUtmSources.map(source => (
                      <option key={source} value={source}>{source}</option>
                    ))}
                  </select>
                </div>
                
                {/* Min Pages Filter */}
                <div>
                  <select
                    value={minPagesFilter}
                    onChange={(e) => setMinPagesFilter(Number(e.target.value))}
                    className="w-full px-3 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100"
                  >
                    <option value="0">Any pages</option>
                    <option value="2">2+ pages</option>
                    <option value="3">3+ pages</option>
                    <option value="5">5+ pages</option>
                    <option value="10">10+ pages</option>
                  </select>
                </div>
              </div>
              
              {/* Filter Stats */}
              <div className="mt-2 flex items-center justify-between text-xs text-secondary-600 dark:text-secondary-400">
                <span>Showing {paginatedSessions.length} of {filteredSessions.length} journeys (from {totalSessions} total unique journeys)</span>
                <div className="flex items-center gap-2">
                  <span>Per page:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    className="px-2 py-1 border border-secondary-300 dark:border-secondary-600 rounded bg-white dark:bg-secondary-800"
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Sessions List */}
            <div className="space-y-3">
              {paginatedSessions.map(session => (
              <div
                key={session.sessionId}
                className="border border-secondary-200 dark:border-secondary-700 rounded-xl overflow-hidden hover:border-primary-400 dark:hover:border-primary-600 transition-all"
              >
                {/* Session Header - Clickable */}
                <button
                  onClick={() => handleSessionToggle(session)}
                  className="w-full p-4 flex items-center gap-4 hover:bg-secondary-50 dark:hover:bg-secondary-800/50 transition-colors text-left"
                >
                  <div className="flex-shrink-0">{getDeviceIcon(session.device)}</div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-mono text-sm font-medium">
                        {session.sessionId.substring(0, 16)}...
                      </span>
                      {session.stitchedSessionIds && session.stitchedSessionIds.length > 1 && (
                        <span className="badge bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs flex items-center gap-1">
                          <Hash className="w-3 h-3" />
                          {session.stitchedSessionIds.length} sessions merged
                        </span>
                      )}
                      {session.userId && (
                        <span className="badge badge-primary text-xs">User: {session.userId}</span>
                      )}
                      <span className="badge badge-secondary text-xs">{session.device}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-secondary-600 dark:text-secondary-400">
                      <span>{session.totalPages} pages</span>
                      {session.duration && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDuration(session.duration)}
                        </span>
                      )}
                      {session.utmSource && session.utmSource !== 'direct' && (
                        <span className="text-blue-600 dark:text-blue-400">
                          {session.utmSource}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    {expandedSession === session.sessionId ? (
                      <ChevronDown className="w-5 h-5 text-primary-600" />
                    ) : (
                      <ChevronRight className="w-5 h-5" />
                    )}
                  </div>
                </button>

                {/* Expanded Session Details */}
                {expandedSession === session.sessionId && selectedSessionData && (
                  <div className="border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-900/50 animate-slide-down">
                    
                    {/* Show Stitched Sessions Info if applicable */}
                    {session.stitchedSessionIds && session.stitchedSessionIds.length > 1 && (
                      <div className="px-4 pt-4 pb-3 bg-amber-50 dark:bg-amber-900/20 border-b-2 border-amber-300 dark:border-amber-700">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-0.5">
                            <Hash className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-bold text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
                              ⚠️ AUTO-MERGED: {session.stitchedSessionIds.length} Separate Sessions Combined
                            </div>
                            
                            <div className="mb-2 px-3 py-2 bg-amber-100 dark:bg-amber-900/40 rounded border border-amber-300 dark:border-amber-600">
                              <div className="text-xs text-amber-700 dark:text-amber-300 mb-1">
                                <strong>Why merged:</strong> Backend detected same user continuing journey:
                              </div>
                              <ul className="text-xs text-amber-700 dark:text-amber-300 list-disc list-inside space-y-0.5">
                                <li>Same device fingerprint OR user ID</li>
                                <li>Visited within 2 minutes</li>
                                <li>Same UTM campaign (if present)</li>
                                <li>Cross-domain continuation (landing → main site)</li>
                              </ul>
                            </div>
                            
                            <div className="text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">
                              Original session IDs that were merged:
                            </div>
                            <div className="text-xs text-amber-600 dark:text-amber-400 space-y-1 mb-2">
                              {session.stitchedSessionIds.map((sid: string, idx: number) => (
                                <div key={sid} className="font-mono flex items-center gap-2 pl-2">
                                  <span className="opacity-60">{idx + 1}.</span>
                                  <span className="bg-amber-200 dark:bg-amber-800 px-2 py-0.5 rounded">{sid}</span>
                                  {sid === session.sessionId && (
                                    <span className="badge bg-amber-300 dark:bg-amber-700 text-amber-900 dark:text-amber-100 text-xs font-bold">Primary</span>
                                  )}
                                </div>
                              ))}
                            </div>
                            <div className="mt-2 px-2 py-1.5 bg-amber-200 dark:bg-amber-900/60 rounded border border-amber-400 dark:border-amber-600 text-xs text-amber-800 dark:text-amber-200">
                              <strong>Note:</strong> Pages below show the <strong>COMBINED journey</strong> from ALL {session.stitchedSessionIds.length} sessions merged together. If you want to see each session separately, you need to disable session stitching in the backend.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="p-4">
                    {(() => {
                      // **DEBUG: Log received session data**
                      console.log('🔍 SessionDrillDownModal - selectedSessionData:', {
                        sessionId: selectedSessionData.sessionId,
                        hasEvents: !!selectedSessionData.events,
                        eventsCount: selectedSessionData.events?.length || 0,
                        hasSankey: !!selectedSessionData.sankey,
                        summary: selectedSessionData.summary,
                        rawData: selectedSessionData, // Log entire object to see structure
                      });

                      if (selectedSessionData.events) {
                        console.log(
                          '✅ Events array exists:',
                          selectedSessionData.events.length,
                          'events'
                        );
                        console.log('📄 First 3 events:', selectedSessionData.events.slice(0, 3));
                      } else {
                        console.error(
                          '❌ NO EVENTS ARRAY! selectedSessionData:',
                          selectedSessionData
                        );
                      }

                      // Transform events to pages with FULL context
                      // Backend already filters for "Page Load" events only, so no need to filter here
                      const pages = selectedSessionData.events
                        ? selectedSessionData.events.map((event: any) => {
                            // Extract website from page_url or website field
                            let website = '';
                            try {
                              if (event.website) {
                                website = event.website;
                              } else if (event.page_url) {
                                const url = new URL(
                                  event.page_url.startsWith('http')
                                    ? event.page_url
                                    : `https://${event.page_url}`
                                );
                                website = url.hostname.replace('www.', '');
                              }
                            } catch {
                              website = event.website || 'unknown';
                            }

                            return {
                              url: event.page_url,
                              title: event.page_title,
                              timestamp: event.timestamp,
                              website: website,
                              event: event.event || 'Page Load', // Event type
                              sessionId: event.session_id || null,
                              // All UTM parameters
                              utmSource: event.utm_source || null,
                              utmMedium: event.utm_medium || null,
                              utmCampaign: event.utm_campaign || null,
                              utmTerm: event.utm_term || null,
                              utmContent: event.utm_content || null,
                              gadSource: event.gad_source || null,
                              // Technical details
                              browser: event.browser || null,
                              os: event.os || null,
                              deviceType: event.device_type || null,
                              screenResolution: event.screen_resolution || null,
                              // Performance metrics
                              pageStartTime: event.page_start_time || null,
                              pageLoadTime: event.page_load_time || null,
                              pageLoadDuration: event.page_load_duration || null,
                            };
                          })
                        : selectedSessionData.pages || [];

                      return pages.length > 0 ? (
                        <div>
                          {/* Session Stats */}
                          <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="p-3 bg-white dark:bg-secondary-800 rounded-lg shadow-sm">
                              <p className="text-xs text-secondary-600 dark:text-secondary-400 flex items-center gap-1">
                                {getDeviceIcon(selectedSessionData.summary.device)}
                                Device
                              </p>
                              <p className="font-semibold text-sm mt-1">
                                {selectedSessionData.summary.device}
                              </p>
                            </div>
                            <div className="p-3 bg-white dark:bg-secondary-800 rounded-lg shadow-sm">
                              <p className="text-xs text-secondary-600 dark:text-secondary-400 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Duration
                              </p>
                              <p className="font-semibold text-sm mt-1">
                                {formatDuration(selectedSessionData.summary.duration)}
                              </p>
                            </div>
                            <div className="p-3 bg-white dark:bg-secondary-800 rounded-lg shadow-sm">
                              <p className="text-xs text-secondary-600 dark:text-secondary-400 flex items-center gap-1">
                                <Hash className="w-3 h-3" />
                                Pages
                              </p>
                              <p className="font-semibold text-sm mt-1">{pages.length}</p>
                            </div>
                          </div>

                          <h4 className="font-semibold text-sm text-secondary-900 dark:text-secondary-50 mb-3 flex items-center gap-2">
                            <ChevronRight className="w-4 h-4" />
                            Complete User Journey ({pages.length} pages)
                          </h4>

                          {/* Step-by-Step Path Visualization - Accordion */}
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {pages.map((page: any, index: number) => (
                              <div
                                key={index}
                                className="border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden bg-white dark:bg-secondary-800"
                              >
                                {/* Accordion Header - Clickable */}
                                <button
                                  onClick={() =>
                                    setExpandedPageIndex(expandedPageIndex === index ? null : index)
                                  }
                                  className="w-full flex items-start gap-3 p-3 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors text-left"
                                >
                                  {/* Step Number */}
                                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold text-sm">
                                    {index + 1}
                                  </div>

                                  {/* Page Title & Basic Info */}
                                  <div className="flex-1 min-w-0">
                                    <p
                                      className="text-sm font-semibold text-secondary-900 dark:text-secondary-50 mb-1 truncate"
                                      title={page.title || page.url}
                                    >
                                      {page.title || page.url}
                                    </p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                                        {page.event}
                                      </span>
                                      {page.timestamp && (
                                        <span className="text-xs text-secondary-500 dark:text-secondary-400">
                                          {new Date(page.timestamp).toLocaleTimeString()}
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Expand/Collapse Icon */}
                                  <div className="flex-shrink-0">
                                    {expandedPageIndex === index ? (
                                      <ChevronDown className="w-5 h-5 text-primary-600" />
                                    ) : (
                                      <ChevronRight className="w-5 h-5" />
                                    )}
                                  </div>
                                </button>

                                {/* Accordion Content - Expandable */}
                                {expandedPageIndex === index && (
                                  <div className="border-t border-secondary-200 dark:border-secondary-700 p-3 bg-secondary-50 dark:bg-secondary-900/50 animate-slide-down">
                                    {/* Event & Session ID - Compact inline */}
                                    <div className="mb-2 space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-secondary-600 dark:text-secondary-400">
                                          📋 Event:
                                        </span>
                                        <span className="text-xs font-medium text-secondary-900 dark:text-secondary-100">
                                          {page.event}
                                        </span>
                                      </div>
                                      {page.sessionId && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs font-medium text-secondary-600 dark:text-secondary-400">
                                            🆔 Session:
                                          </span>
                                          <span className="text-xs font-mono text-secondary-900 dark:text-secondary-100 break-all">
                                            {page.sessionId}
                                          </span>
                                        </div>
                                      )}
                                    </div>

                                    {/* Page URL - Compact */}
                                    <div className="mb-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-secondary-600 dark:text-secondary-400">
                                          🔗 URL:
                                        </span>
                                        <span className="text-xs text-secondary-900 dark:text-secondary-100 break-all">
                                          {page.url}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Organized sections with labels - Balanced compact */}
                                    <div className="space-y-2">
                                      {/* Device & Display Section */}
                                      <div>
                                        <p className="text-xs font-semibold text-secondary-700 dark:text-secondary-300 mb-1">
                                          Device & Display:
                                        </p>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          {page.website && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                                              🌐 Site: {page.website}
                                            </span>
                                          )}
                                          {page.deviceType && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300">
                                              📱 Device: {page.deviceType}
                                            </span>
                                          )}
                                          {page.screenResolution && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300">
                                              🖥️ Screen: {page.screenResolution}
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      {/* UTM Parameters Section */}
                                      {(page.utmSource ||
                                        page.utmMedium ||
                                        page.utmCampaign ||
                                        page.utmTerm ||
                                        page.utmContent ||
                                        page.gadSource) && (
                                        <div>
                                          <p className="text-xs font-semibold text-secondary-700 dark:text-secondary-300 mb-1">
                                            UTM Parameters:
                                          </p>
                                          <div className="flex items-center gap-2 flex-wrap">
                                            {page.utmSource && page.utmSource !== 'direct' && (
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                                                📍 Source: {page.utmSource}
                                              </span>
                                            )}
                                            {page.utmMedium && (
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300">
                                                📡 Medium: {page.utmMedium}
                                              </span>
                                            )}
                                            {page.utmCampaign && (
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                                                🎯 Campaign: {page.utmCampaign}
                                              </span>
                                            )}
                                            {page.utmTerm && (
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300">
                                                🔑 Term: {page.utmTerm}
                                              </span>
                                            )}
                                            {page.utmContent && (
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-300">
                                                🎨 Content: {page.utmContent}
                                              </span>
                                            )}
                                            {page.gadSource && (
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                                                💰 GAD: {page.gadSource}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Technical Details Section */}
                                      {(page.browser || page.os) && (
                                        <div>
                                          <p className="text-xs font-semibold text-secondary-700 dark:text-secondary-300 mb-1">
                                            Browser & OS:
                                          </p>
                                          <div className="flex items-center gap-2 flex-wrap">
                                            {page.browser && (
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                                                🌐 Browser: {page.browser}
                                              </span>
                                            )}
                                            {page.os && (
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300">
                                                💻 OS: {page.os}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Performance & Timestamp Section */}
                                      <div>
                                        <p className="text-xs font-semibold text-secondary-700 dark:text-secondary-300 mb-1">
                                          Performance & Time:
                                        </p>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          {page.pageLoadDuration !== null && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">
                                              ⚡ Load Time:{' '}
                                              {(page.pageLoadDuration / 1000).toFixed(2)}s
                                            </span>
                                          )}
                                          {page.timestamp && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300">
                                              🕐 Time:{' '}
                                              {new Date(page.timestamp).toLocaleTimeString()}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Journey Summary */}
                          {pages.length > 1 && (
                            <div className="mt-4 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                              <div className="flex items-center gap-2 text-sm flex-wrap">
                                <span className="text-secondary-700 dark:text-secondary-300">
                                  Journey:{' '}
                                  <strong>{pages[0]?.title || pages[0]?.url || 'Start'}</strong>
                                </span>
                                {pages.length > 2 && (
                                  <>
                                    <ChevronRight className="w-4 h-4 text-secondary-400" />
                                    <span className="text-secondary-700 dark:text-secondary-300">
                                      ... {pages.length - 2} pages ...
                                    </span>
                                  </>
                                )}
                                <ChevronRight className="w-4 h-4 text-secondary-400" />
                                <span className="text-secondary-700 dark:text-secondary-300">
                                  <strong>
                                    {pages[pages.length - 1]?.title ||
                                      pages[pages.length - 1]?.url ||
                                      'End'}
                                  </strong>
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center py-8">
                          <div className="spinner"></div>
                        </div>
                      );
                    })()}
                    </div>
                  </div>
                )}
              </div>
            ))}
            </div>

            {/* **NEW: Pagination Controls** */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-secondary-200 dark:border-secondary-700">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50 dark:hover:bg-secondary-700"
                >
                  ← Previous
                </button>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-secondary-600 dark:text-secondary-400">
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  {/* Page number buttons (show max 5) */}
                  <div className="hidden md:flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      // Show first 2, current, and last 2 pages
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1 text-sm rounded ${
                            currentPage === pageNum
                              ? 'bg-primary-600 text-white'
                              : 'bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100 border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-50 dark:hover:bg-secondary-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50 dark:hover:bg-secondary-700"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-secondary-400">
            <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No sessions found</p>
            <p className="text-sm mt-2">Try adjusting your filters</p>
          </div>
        )}
      </div>
    </>
  );

  // Desktop: Full Modal
  if (!isMobile) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center animate-fade-in">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

        {/* Modal */}
        <div className="relative w-full max-w-6xl mx-4 bg-white dark:bg-secondary-900 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col animate-scale-up overflow-hidden">
          {modalContent}
        </div>
      </div>
    );
  }

  // Mobile/Tablet: Bottom Drawer
  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center animate-fade-in">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div className="relative w-full bg-white dark:bg-secondary-900 rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col animate-slide-up-mobile overflow-hidden">
        {/* Pull indicator */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1 bg-secondary-300 dark:bg-secondary-700 rounded-full" />
        </div>

        {modalContent}
      </div>
    </div>
  );
};

export default SessionDrillDownModal;
